// ============================================================
// SpringBootDIBeans.jsx
//
// A branded, responsive course content page for the
// "Dependency Injection & Beans" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Inversion of Control (IoC) patterns, injection
//           strategies, and bean lifecycle hooks.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import PsychologyIcon        from "@mui/icons-material/Psychology";
import ExtensionIcon         from "@mui/icons-material/Extension";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import HubIcon               from "@mui/icons-material/Hub";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootDIBeans() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 03"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Dependency <span>Injection & Beans</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Spring Boot manages object creation and dependency resolution automatically using its Inversion of Control (IoC) container. This decoupled architecture makes your code cleaner, modular, and easily testable.
                    </Typography>
                </Box>

                {/* 1. DI EXPLAINED */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PsychologyIcon />} label="1. Understanding Dependency Injection" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Instead of your classes being responsible for creating their own dependencies, the Spring IoC container "injects" them at runtime. This reverses the control of object instantiation.
                    </Typography>
                    <CodeBlock
                        title="Manual vs Spring Managed"
                        code={`// TRADITIONAL (Tight Coupling)\nUserService service = new UserService();\n\n// SPRING MANAGED (Loose Coupling)\n@Autowired\nprivate UserService userService;`}
                    />
                </Box>

                {/* 2. INJECTION TYPES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsInputComponentIcon />} label="2. Injection Strategies" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        While field injection is common, <strong>Constructor Injection</strong> is the industry standard for production code because it allows for final fields and easier unit testing.
                    </Typography>
                    <CodeBlock
                        title="Constructor Injection (Preferred)"
                        code={`@Component\npublic class OrderService {\n    private final PaymentService paymentService;\n\n    // Autowired automatically if only one constructor exists\n    public OrderService(PaymentService paymentService) {\n        this.paymentService = paymentService;\n    }\n}`}
                    />
                </Box>

                {/* 3. BEAN STEREOTYPES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HubIcon />} label="3. Declaring Managed Beans" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Spring uses specific annotations to identify which classes should be managed as "Beans" within the container.
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { title: "@Component", desc: "Generic stereotype for any Spring-managed component." },
                            { title: "@Service", desc: "Specialized for business logic and service layer." },
                            { title: "@Repository", desc: "For data access; includes automatic exception translation." },
                            { title: "@Configuration", desc: "Indicates a class that declares one or more @Bean methods." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontFamily: "monospace", fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. LIFECYCLE & SCOPE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ExtensionIcon />} label="4. Bean Scopes & Lifecycle" />
                    <CodeBlock
                        title="Advanced Bean Config"
                        code={`@Component\n@Scope("prototype") // New instance per request (Default is "singleton")\npublic class MyBean {\n\n    @PostConstruct\n    public void init() { /* Runs after DI */ }\n\n    @PreDestroy\n    public void cleanup() { /* Runs before destruction */ }\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* SUMMARY CHECKLIST */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Best Practices Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always prefer Constructor Injection for mandatory dependencies",
                            "Use final fields to ensure thread safety and immutability",
                            "Use specific stereotypes (@Service, @Repository) for semantic clarity",
                            "Avoid Field Injection (@Autowired on private fields) in complex apps",
                            "Utilize @PostConstruct for logic that requires injected beans",
                            "Understand that Singleton is the default scope for all beans"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Constructor injection makes your dependencies <strong>final</strong> and your classes easier to unit test, as you can pass mock objects directly into the constructor without needing to mock the Spring context.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
